import React, { ReactElement } from 'react';

import PropTypes from 'prop-types';

import { useTheme } from '@adsk/alloy-react-styles';
import {
  stylePropType,
  StylableComponent,
  type StyleProp,
} from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

export type DetailsProps = StylableComponent<HTMLDivElement> & {
  title?: string;
  subtitle?: string;
  tertiaryText?: string;
  renderIcons?: () => ReactElement;
  renderActions?: () => ReactElement;
  selected?: boolean;
  /** Style applied to the title container element, merged after token defaults. */
  titleStyle?: StyleProp;
  /**
   * Style applied to the subtitle container element, merged after token defaults.
   * Has no effect when `subtitle` is not provided.
   */
  subtitleStyle?: StyleProp;
  /** Style applied to the metadata container element (tertiaryText + icons), merged after token defaults. */
  metadataStyle?: StyleProp;
};

const Details = React.forwardRef<HTMLDivElement, DetailsProps>(
  (
    {
      style,
      className,
      title,
      subtitle,
      tertiaryText,
      renderIcons,
      renderActions,
      selected,
      titleStyle,
      subtitleStyle,
      metadataStyle,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const tileTokens = theme.tokens?.component?.tile;
    const contentContainer = tileTokens?.['content-container'];
    const titleTokens = tileTokens?.title;
    const subtitleTokens = tileTokens?.subtitle;
    const metadataTokens = tileTokens?.metadata;

    const detailsBorderColor = selected
      ? contentContainer?.borderColor?.selected
      : contentContainer?.borderColor?.default;

    if (
      !title &&
      !subtitle &&
      !tertiaryText &&
      !renderIcons &&
      !renderActions
    ) {
      return null;
    }

    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          {
            paddingTop: contentContainer?.paddingTop,
            paddingRight: contentContainer?.paddingRight,
            paddingBottom: contentContainer?.paddingBottom,
            paddingLeft: contentContainer?.paddingLeft,
            borderBottomLeftRadius: contentContainer?.borderBottomLeftRadius,
            borderBottomRightRadius: contentContainer?.borderBottomRightRadius,
            borderLeftWidth: contentContainer?.borderWidth,
            borderRightWidth: contentContainer?.borderWidth,
            borderBottomWidth: contentContainer?.borderWidth,
            borderLeftStyle: contentContainer?.borderStyle,
            borderRightStyle: contentContainer?.borderStyle,
            borderBottomStyle: contentContainer?.borderStyle,
            borderLeftColor: detailsBorderColor,
            borderRightColor: detailsBorderColor,
            borderBottomColor: detailsBorderColor,
          },
          style,
        ]}
      >
        {(title || renderActions) && (
          <div
            data-testid="Tile__title-container"
            css={[
              {
                minHeight: titleTokens?.minHeight,
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: titleTokens?.paddingTop,
                paddingRight: titleTokens?.paddingRight,
                paddingBottom: titleTokens?.paddingBottom,
                paddingLeft: titleTokens?.paddingLeft,
                color: titleTokens?.color,
                fontFamily: titleTokens?.fontFamily,
                fontSize: titleTokens?.fontSize,
                fontWeight: titleTokens?.fontWeight,
                lineHeight: titleTokens?.lineHeight,
              },
              titleStyle,
            ]}
          >
            <div
              css={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <OverflowTooltip>{title}</OverflowTooltip>
            </div>
            {renderActions?.()}
          </div>
        )}
        {subtitle && (
          <div
            data-testid="Tile__subtitle-container"
            css={[
              {
                marginTop: subtitleTokens?.marginTop,
                paddingTop: subtitleTokens?.paddingTop,
                paddingRight: subtitleTokens?.paddingRight,
                paddingBottom: subtitleTokens?.paddingBottom,
                paddingLeft: subtitleTokens?.paddingLeft,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                color: subtitleTokens?.color,
                fontFamily: subtitleTokens?.fontFamily,
                fontSize: subtitleTokens?.fontSize,
                fontWeight: subtitleTokens?.fontWeight,
                lineHeight: subtitleTokens?.lineHeight,
                textOverflow: 'ellipsis',
              },
              subtitleStyle,
            ]}
          >
            <OverflowTooltip>{subtitle}</OverflowTooltip>
          </div>
        )}
        {(tertiaryText || renderIcons) && (
          <div
            data-testid="Tile__metadata-container"
            css={[
              {
                marginTop: metadataTokens?.marginTop,
                paddingTop: metadataTokens?.paddingTop,
                paddingRight: metadataTokens?.paddingRight,
                paddingBottom: metadataTokens?.paddingBottom,
                paddingLeft: metadataTokens?.paddingLeft,
                display: 'flex',
                justifyContent: 'space-between',
                gap: metadataTokens?.gap,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                color: metadataTokens?.color,
                fontFamily: metadataTokens?.fontFamily,
                fontSize: metadataTokens?.fontSize,
                fontWeight: metadataTokens?.fontWeight,
                lineHeight: metadataTokens?.lineHeight,
              },
              metadataStyle,
            ]}
          >
            <div
              css={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <OverflowTooltip>{tertiaryText}</OverflowTooltip>
            </div>
            {renderIcons?.()}
          </div>
        )}
      </div>
    );
  },
);

Details.displayName = 'Details';

Details.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Tile title */
  title: PropTypes.string,
  /** Tile subtitle */
  subtitle: PropTypes.string,
  /** Tile tertiary text */
  tertiaryText: PropTypes.string,
  /** Render icons function */
  renderIcons: PropTypes.func,
  /** Render actions.  Generally used to render a three dot ActionButton */
  renderActions: PropTypes.func,
  /** If Tile is selected. */
  selected: PropTypes.bool,
  /** Style applied to the title container element, merged after token defaults. */
  titleStyle: stylePropType,
  /** Style applied to the subtitle container element, merged after token defaults. */
  subtitleStyle: stylePropType,
  /** Style applied to the metadata container element (tertiaryText + icons), merged after token defaults. */
  metadataStyle: stylePropType,
};

export default Details;
